import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import Spinner from "react-bootstrap/Spinner";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import {
  FaSearch, FaEnvelope, FaHome, FaFilter, FaChartLine, FaFileAlt,
  FaSignInAlt, FaUserCheck, FaClipboardCheck, FaPencilAlt, FaEye,
  FaMapMarkerAlt, FaBell, FaPaperPlane, FaMousePointer, FaEnvelopeOpen,
  FaExclamationTriangle, FaBan, FaCog, FaChevronDown, FaTimes,
  FaCalendarAlt, FaDownload, FaShareAlt, FaArrowsAlt, FaKey,
  FaScroll, FaBookOpen, FaTimesCircle, FaBed, FaBath, FaCar,
  FaRulerCombined, FaBuilding, FaHeart, FaNewspaper, FaDoorOpen,
  FaSms, FaCommentSlash,
} from "react-icons/fa";
import { handleTokenAndPostRequest } from "../utils/utils";
import { useViewScope } from "../contexts/ViewScopeContext";
import DemoBanner from "../demo/DemoBanner";
import useDemoNetworkGuard from "../demo/useDemoNetworkGuard";

// ── Event config ──────────────────────────────────────────────
export const EMAIL_EVENT_CONFIG = {
  Delivery:           { label: "Delivered",   bg: "#dbeafe", color: "#1d4ed8", border: "#93c5fd", Icon: FaPaperPlane, desc: "Email was successfully delivered to the recipient's inbox" },
  Open:               { label: "Opened",      bg: "#d1fae5", color: "#047857", border: "#6ee7b7", Icon: FaEnvelopeOpen, desc: "Recipient opened the email" },
  Click:              { label: "Clicked",      bg: "#e0e7ff", color: "#4338ca", border: "#a5b4fc", Icon: FaMousePointer, desc: "Recipient clicked a link in the email" },
  Bounce:             { label: "Bounced",      bg: "#fee2e2", color: "#b91c1c", border: "#fca5a5", Icon: FaExclamationTriangle, desc: "Email bounced — could not be delivered to this address" },
  SpamComplaint:      { label: "Spam",         bg: "#fef2f2", color: "#991b1b", border: "#fecaca", Icon: FaBan, desc: "Recipient marked the email as spam" },
  SubscriptionChange: { label: "Unsubscribed", bg: "#fef3c7", color: "#92400e", border: "#fde68a", Icon: FaBell, desc: "Recipient unsubscribed from future emails" },
};

export const PLATFORM_EVENT_CONFIG = {
  property_analysed:         { label: "Property Analysed",       bg: "#f3e8ff", color: "#7c3aed", border: "#c4b5fd", Icon: FaChartLine,       desc: "Viewed the property analysis results" },
  read_user_settings:        { label: "Read Settings",           bg: "#e0f2fe", color: "#0369a1", border: "#7dd3fc", Icon: FaCog,             desc: "Loaded their saved notification preferences" },
  scroll_depth:              { label: "Scroll Depth",            bg: "#fef9c3", color: "#a16207", border: "#fde047", Icon: FaScroll,           desc: "Scrolled through an article or property page (25%, 50%, 75%)" },
  send_property_report:      { label: "Report Sent",             bg: "#ede9fe", color: "#6d28d9", border: "#a78bfa", Icon: FaFileAlt,          desc: "Property report was generated and sent to the contact" },
  click_nav:                 { label: "Navigation Click",        bg: "#f1f5f9", color: "#475569", border: "#cbd5e1", Icon: FaMousePointer,     desc: "Clicked a main navigation tab (reports, valuation, suburbs, etc.)" },
  analysis_started:          { label: "Analysis Started",        bg: "#fce7f3", color: "#be185d", border: "#f9a8d4", Icon: FaChartLine,        desc: "Started a new property analysis from the valuation tool" },
  click_analyse_property:    { label: "Analyse Property",        bg: "#e0e7ff", color: "#4338ca", border: "#a5b4fc", Icon: FaChartLine,        desc: "Clicked the Analyse button to run a property comparison" },
  unsubscribe:               { label: "Unsubscribed",            bg: "#fee2e2", color: "#b91c1c", border: "#fca5a5", Icon: FaBan,              desc: "Unsubscribed from email campaigns via the unsubscribe page" },
  change_bedrooms:           { label: "Changed Bedrooms",        bg: "#fff7ed", color: "#c2410c", border: "#fed7aa", Icon: FaBed,              desc: "Adjusted the bedroom count in the property editor" },
  change_bathrooms:          { label: "Changed Bathrooms",       bg: "#fff7ed", color: "#c2410c", border: "#fed7aa", Icon: FaBath,             desc: "Adjusted the bathroom count in the property editor" },
  change_carspaces:          { label: "Changed Car Spaces",      bg: "#fff7ed", color: "#c2410c", border: "#fed7aa", Icon: FaCar,              desc: "Adjusted the car space count in the property editor" },
  get_article_by_agency:     { label: "Viewed Agency Article",   bg: "#ecfdf5", color: "#047857", border: "#6ee7b7", Icon: FaNewspaper,        desc: "Viewed a news article published by the agency" },
  modal_open_sign_in:        { label: "Sign-in Opened",          bg: "#dbeafe", color: "#1d4ed8", border: "#93c5fd", Icon: FaDoorOpen,         desc: "Opened the sign-in modal (triggered by a protected action)" },
  modal_close_sign_in:       { label: "Sign-in Closed",          bg: "#f1f5f9", color: "#64748b", border: "#cbd5e1", Icon: FaTimesCircle,      desc: "Closed the sign-in modal without completing authentication" },
  click_track_property:      { label: "Track Property",          bg: "#fce7f3", color: "#be185d", border: "#f9a8d4", Icon: FaHeart,            desc: "Clicked to add a property to their tracked/watchlist" },
  save_notification_settings:{ label: "Saved Settings",          bg: "#d1fae5", color: "#047857", border: "#6ee7b7", Icon: FaCog,              desc: "Saved their notification preferences (frequency, channels, properties)" },
  click_sign_in_nav:         { label: "Sign-in (Nav)",           bg: "#dbeafe", color: "#1d4ed8", border: "#93c5fd", Icon: FaSignInAlt,        desc: "Clicked Sign In from the main navigation bar" },
  view_appraisal:            { label: "Viewed Appraisal",        bg: "#fff1f2", color: "#be123c", border: "#fda4af", Icon: FaEye,              desc: "Viewed the full property appraisal / valuation estimate" },
  click_sign_in_submit:      { label: "Email Submitted",         bg: "#e0e7ff", color: "#4338ca", border: "#a5b4fc", Icon: FaSignInAlt,        desc: "Submitted their email address to start the sign-in flow" },
  get_article:               { label: "Viewed Article",          bg: "#ecfdf5", color: "#047857", border: "#6ee7b7", Icon: FaBookOpen,         desc: "Viewed a news or market update article" },
  click_edit_property_details:{ label: "Edit Property",          bg: "#fef3c7", color: "#92400e", border: "#fde68a", Icon: FaPencilAlt,        desc: "Clicked Edit Details to modify property specs (beds, baths, etc.)" },
  click_view_appraisal:      { label: "View Appraisal",         bg: "#fff1f2", color: "#be123c", border: "#fda4af", Icon: FaEye,              desc: "Clicked the button to view the full appraisal details" },
  change_property_type:      { label: "Changed Type",            bg: "#fef3c7", color: "#92400e", border: "#fde68a", Icon: FaBuilding,         desc: "Changed the property type (house, apartment, land, etc.)" },
  modal_close_share:         { label: "Share Closed",            bg: "#f1f5f9", color: "#64748b", border: "#cbd5e1", Icon: FaTimesCircle,      desc: "Closed the share valuation modal without sharing" },
  change_land_area:          { label: "Changed Land Area",       bg: "#fff7ed", color: "#c2410c", border: "#fed7aa", Icon: FaRulerCombined,    desc: "Modified the land area (m²) in the property editor" },
  click_share_valuation:     { label: "Share Valuation",         bg: "#ede9fe", color: "#6d28d9", border: "#a78bfa", Icon: FaShareAlt,         desc: "Clicked Share to share the property valuation with someone" },
  submit_sign_in_code:       { label: "Code Submitted",          bg: "#d1fae5", color: "#047857", border: "#6ee7b7", Icon: FaKey,              desc: "Submitted the 6-digit verification code to complete sign-in" },
  sms_sent:                  { label: "SMS Sent",                bg: "#dcfce7", color: "#166534", border: "#86efac", Icon: FaSms,              desc: "SMS was successfully delivered to the recipient" },
  sms_unsubscribe:           { label: "SMS Unsubscribed",        bg: "#fee2e2", color: "#b91c1c", border: "#fca5a5", Icon: FaCommentSlash,     desc: "Recipient replied STOP to unsubscribe from SMS" },
};

export const CAMPAIGN_CONFIG = {
  market_update:      { label: "Market Update",       bg: "#dbeafe", color: "#1d4ed8", border: "#93c5fd" },
  property_update:    { label: "Property Update",     bg: "#d1fae5", color: "#047857", border: "#6ee7b7" },
  onboarding_welcome: { label: "Onboarding Welcome",  bg: "#fef3c7", color: "#92400e", border: "#fde68a" },
  property_report:    { label: "Property Report",     bg: "#ede9fe", color: "#6d28d9", border: "#a78bfa" },
  agent_property_note:{ label: "Agent Note",          bg: "#fff1f2", color: "#be123c", border: "#fda4af" },
  share_property:     { label: "Share Property",      bg: "#e0f2fe", color: "#0369a1", border: "#7dd3fc" },
  appraisal_send:     { label: "Appraisal",           bg: "#fce7f3", color: "#be185d", border: "#f9a8d4" },
};

export const CAMPAIGN_OPTIONS = [
  { value: "", label: "All campaigns" },
  { value: "market_update", label: "Market Update" },
  { value: "property_update", label: "Property Update" },
  { value: "onboarding_welcome", label: "Onboarding Welcome" },
  { value: "property_report", label: "Property Report" },
  { value: "agent_property_note", label: "Agent Note" },
  { value: "share_property", label: "Share Property" },
  { value: "appraisal_send", label: "Appraisal" },
];

export const EMAIL_EVENT_OPTIONS = [
  { value: "Delivery", label: "Delivered" },
  { value: "Open", label: "Opened" },
  { value: "Click", label: "Clicked" },
  { value: "Bounce", label: "Bounced" },
  { value: "SpamComplaint", label: "Spam" },
  { value: "SubscriptionChange", label: "Unsubscribed" },
];

export const PLATFORM_EVENT_OPTIONS = [
  { value: "property_analysed", label: "Property Analysed" },
  { value: "analysis_started", label: "Analysis Started" },
  { value: "click_analyse_property", label: "Analyse Property" },
  { value: "view_appraisal", label: "Viewed Appraisal" },
  { value: "click_view_appraisal", label: "View Appraisal" },
  { value: "send_property_report", label: "Report Sent" },
  { value: "click_track_property", label: "Track Property" },
  { value: "click_share_valuation", label: "Share Valuation" },
  { value: "click_edit_property_details", label: "Edit Property" },
  { value: "change_bedrooms", label: "Changed Bedrooms" },
  { value: "change_bathrooms", label: "Changed Bathrooms" },
  { value: "change_carspaces", label: "Changed Car Spaces" },
  { value: "change_land_area", label: "Changed Land Area" },
  { value: "change_property_type", label: "Changed Type" },
  { value: "click_nav", label: "Navigation Click" },
  { value: "scroll_depth", label: "Scroll Depth" },
  { value: "get_article", label: "Viewed Article" },
  { value: "get_article_by_agency", label: "Viewed Agency Article" },
  { value: "modal_open_sign_in", label: "Sign-in Opened" },
  { value: "modal_close_sign_in", label: "Sign-in Closed" },
  { value: "click_sign_in_nav", label: "Sign-in (Nav)" },
  { value: "click_sign_in_submit", label: "Email Submitted" },
  { value: "submit_sign_in_code", label: "Code Submitted" },
  { value: "save_notification_settings", label: "Saved Settings" },
  { value: "read_user_settings", label: "Read Settings" },
  { value: "unsubscribe", label: "Unsubscribed" },
  { value: "modal_close_share", label: "Share Closed" },
];

export const SMS_EVENT_OPTIONS = [
  { value: "sms_sent", label: "SMS Sent" },
  { value: "sms_unsubscribe", label: "SMS Unsubscribed" },
];

export const DATE_RANGE_OPTIONS = [
  { value: "all", label: "All time" },
  { value: "1", label: "Today" },
  { value: "2", label: "Yesterday" },
  { value: "7", label: "Last 7 days" },
  { value: "28", label: "Last 28 days" },
  { value: "45", label: "Last 45 days" },
  { value: "90", label: "Last 90 days" },
  { value: "180", label: "Last 180 days" },
  { value: "365", label: "Last 365 days" },
];

export const PAGE_SIZE = 50;

export function formatEventBadge(eventType, isPlatform) {
  if (isPlatform) {
    const cfg = PLATFORM_EVENT_CONFIG[eventType] || { label: eventType, bg: "#f1f5f9", color: "#475569", border: "#cbd5e1", Icon: FaBell };
    return cfg;
  }
  return EMAIL_EVENT_CONFIG[eventType] || { label: eventType, bg: "#f1f5f9", color: "#475569", border: "#cbd5e1", Icon: FaEnvelope };
}

export function formatTimestamp(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return d.toLocaleDateString("en-AU", {
    day: "numeric", month: "short", year: "numeric",
  });
}

export function formatTime(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d)) return "";
  return d.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true });
}

export function formatRelative(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return "";
}

export function normaliseCampaign(tag) {
  if (!tag) return "";
  const t = tag.toLowerCase().replace(/\s+/g, "_");
  if (t === "market_update") return "market_update";
  if (t === "property_update") return "property_update";
  if (t === "onboarding_welcome") return "onboarding_welcome";
  if (t === "property_report") return "property_report";
  if (t === "agent_property_note") return "agent_property_note";
  if (t === "share_property") return "share_property";
  if (t === "appraisal_send") return "appraisal_send";
  return tag;
}

export function campaignLabel(tag) {
  const n = normaliseCampaign(tag);
  const cfg = CAMPAIGN_CONFIG[n];
  return cfg ? cfg.label : (tag || "");
}

// ── Demo data for the empty-state preview ────────────────────
// Renders rich sample activity when the real fetch returns nothing and the
// user hasn't applied any filter, so agents immediately understand what the
// page will look like once their campaigns generate engagement. Sandboxed by
// useDemoNetworkGuard while it's on screen.
function _generateDemoData({ campaign, event_types, date_range, email_search, offset, limit }) {
  const now = new Date();
  const rangeDays = date_range === 'all' ? 365 : parseInt(date_range, 10) || 7;
  const slug = 'demo-agent';

  // Realistic Australian contacts
  const contacts = [
    { email: 'sarah.mitchell@gmail.com', first: 'Sarah', addr: '14 Banksia Ave, Cronulla NSW 2230' },
    { email: 'james.nguyen@outlook.com', first: 'James', addr: '7/22 Marine Pde, Maroubra NSW 2035' },
    { email: 'emma.thompson@yahoo.com.au', first: 'Emma', addr: '38 Wattle St, Haberfield NSW 2045' },
    { email: 'david.chen@icloud.com', first: 'David', addr: '5 Harbour View Cres, Lavender Bay NSW 2060' },
    { email: 'olivia.russo@gmail.com', first: 'Olivia', addr: '91 Beachside Dr, Bondi Beach NSW 2026' },
    { email: 'liam.patel@hotmail.com', first: 'Liam', addr: '12 Gum Tree Ln, Epping NSW 2121' },
    { email: 'chloe.williams@me.com', first: 'Chloe', addr: '204/18 Park Ave, Zetland NSW 2017' },
    { email: 'ryan.murphy@gmail.com', first: 'Ryan', addr: '3 Ridge St, North Sydney NSW 2060' },
    { email: 'mia.johnson@outlook.com', first: 'Mia', addr: '67 Pacific Hwy, St Leonards NSW 2065' },
    { email: 'tom.barker@yahoo.com', first: 'Tom', addr: '29 Rose St, Chippendale NSW 2008' },
    { email: 'sophie.hall@gmail.com', first: 'Sophie', addr: '15/8 Coogee Bay Rd, Coogee NSW 2034' },
    { email: 'ben.kelly@live.com.au', first: 'Ben', addr: '42 Churchill Ave, Strathfield NSW 2135' },
  ];

  const subjects = {
    market_update: ['Your April Market Update — Cronulla', 'Market Trends: March Wrap-Up', 'New Market Data Available — Your Area', 'April 2026 Property Insights'],
    property_update: ['New Estimate for 14 Banksia Ave', 'Property Update: Price Change Detected', 'Your Property Tracker — April Update', 'Updated Valuation Available'],
    onboarding_welcome: ['Welcome to Your Property Tracker'],
    property_report: ['Your Property Report is Ready', 'Detailed Analysis: 38 Wattle St'],
    appraisal_send: ['Your Free Appraisal — 91 Beachside Dr'],
    share_property: ['Someone shared a property with you'],
    agent_property_note: ['A note about your property from Demo Agent'],
  };

  const emailEventTypes = ['Delivery', 'Open', 'Click', 'Delivery', 'Open', 'Delivery', 'Open', 'Delivery'];
  const campaigns = ['market_update', 'property_update', 'market_update', 'property_update', 'onboarding_welcome', 'property_report', 'appraisal_send', 'market_update'];
  const platformEventTypes = [
    'property_analysed', 'analysis_started', 'click_analyse_property', 'view_appraisal',
    'click_track_property', 'scroll_depth', 'click_nav', 'click_edit_property_details',
    'change_bedrooms', 'save_notification_settings', 'click_sign_in_submit', 'submit_sign_in_code',
    'get_article', 'send_property_report', 'click_share_valuation', 'click_view_appraisal',
  ];

  function randDate(i, total) {
    const d = new Date(now);
    d.setMinutes(d.getMinutes() - Math.floor((i / total) * rangeDays * 24 * 60 + Math.random() * 120));
    return d.toISOString();
  }

  // Generate email events
  let emailRows = [];
  for (let i = 0; i < 48; i++) {
    const c = contacts[i % contacts.length];
    const tag = campaigns[i % campaigns.length];
    const evt = emailEventTypes[i % emailEventTypes.length];
    const subjs = subjects[tag] || subjects.market_update;
    emailRows.push({
      event_type: evt,
      email: c.email,
      created_at: randDate(i, 48),
      subject: subjs[i % subjs.length],
      tag,
      agent_slug: slug,
    });
  }

  // Generate platform events
  let platRows = [];
  for (let i = 0; i < 32; i++) {
    const c = contacts[i % contacts.length];
    const evtType = platformEventTypes[i % platformEventTypes.length];
    const detail = evtType === 'property_analysed' ? `Analysed ${c.addr}` :
                   evtType === 'scroll_depth' ? `Scrolled 75% on property page` :
                   evtType === 'click_nav' ? 'Clicked "Suburbs" tab' :
                   evtType === 'change_bedrooms' ? 'Changed bedrooms to 4' :
                   evtType === 'save_notification_settings' ? 'Saved quarterly email + SMS' :
                   evtType === 'get_article' ? 'Read "Is Now a Good Time to Sell?"' :
                   evtType === 'send_property_report' ? `Report sent for ${c.addr}` :
                   evtType === 'click_share_valuation' ? `Shared valuation for ${c.addr}` :
                   null;
    platRows.push({
      source_table: 'events',
      event_type: evtType,
      email: c.email,
      created_at: randDate(i, 32),
      subject: null,
      tag: null,
      agent_slug: null,
      address_text: ['property_analysed','click_analyse_property','view_appraisal','click_view_appraisal','click_track_property','click_edit_property_details','change_bedrooms','send_property_report','click_share_valuation'].includes(evtType) ? c.addr : null,
      detail,
      page_url: `https://homeprice.site/${slug}`,
    });
  }

  emailRows.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  platRows.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  // Apply filters client-side
  if (campaign) emailRows = emailRows.filter(r => r.tag === campaign);
  if (email_search) {
    const q = email_search.toLowerCase();
    emailRows = emailRows.filter(r => r.email.toLowerCase().includes(q));
    platRows = platRows.filter(r => r.email.toLowerCase().includes(q));
  }
  if (event_types && event_types.length) {
    const etSet = new Set(event_types);
    emailRows = emailRows.filter(r => etSet.has(r.event_type));
    platRows = platRows.filter(r => etSet.has(r.event_type));
  }

  const total = emailRows.length;
  const paged = emailRows.slice(offset, offset + limit);

  return {
    data: paged,
    platform_events: offset === 0 ? platRows : [],
    total,
    offset,
    limit,
    has_more: offset + limit < total,
  };
}

// ── Component ─────────────────────────────────────────────────
export default function ActivityExplorer() {
  const [searchParams] = useSearchParams();
  const { viewScope, agentFilter } = useViewScope();

  const [rows, setRows] = useState([]);
  const [platformEvents, setPlatformEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [offset, setOffset] = useState(0);

  // Filters
  const [campaign, setCampaign] = useState("");
  const [eventTypes, setEventTypes] = useState([]);
  const [dateRange, setDateRange] = useState("7");
  const [emailSearch, setEmailSearch] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [dateDropdownOpen, setDateDropdownOpen] = useState(false);
  const [eventDropdownOpen, setEventDropdownOpen] = useState(false);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const dateDropdownRef = useRef(null);
  const eventDropdownRef = useRef(null);
  const emailInputRef = useRef(null);

  useEffect(() => {
    function handleResize() { setIsMobile(window.innerWidth < 768); }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClick(e) {
      if (dateDropdownRef.current && !dateDropdownRef.current.contains(e.target)) {
        setDateDropdownOpen(false);
      }
      if (eventDropdownRef.current && !eventDropdownRef.current.contains(e.target)) {
        setEventDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Auto-fill email from URL param
  useEffect(() => {
    const emailParam = searchParams.get("email");
    if (emailParam) {
      setContactEmail(emailParam.trim().toLowerCase());
      setEmailSearch(emailParam.trim().toLowerCase());
      setDateRange("90");
    }
  }, [searchParams]);

  const fetchData = useCallback(async (newOffset = 0, append = false) => {
    if (!append) setLoading(true);
    else setLoadingMore(true);
    try {
      const body = {
        campaign,
        event_types: eventTypes,
        date_range: dateRange,
        email_search: contactEmail || emailSearch,
        contact_email: contactEmail,
        offset: newOffset,
        limit: PAGE_SIZE,
        view_scope: viewScope,
        agent_filter: agentFilter,
      };

      let d;
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/activity-explorer`, body
      );
      d = res.data || res;
      const newRows = Array.isArray(d.data) ? d.data : [];
      if (append) {
        setRows(prev => [...prev, ...newRows]);
      } else {
        setRows(newRows);
        setPlatformEvents(Array.isArray(d.platform_events) ? d.platform_events : []);
      }
      setTotal(d.total || 0);
      setHasMore(!!d.has_more);
      setOffset(newOffset);
    } catch (e) {
      if (!append) { setRows([]); setPlatformEvents([]); setTotal(0); setHasMore(false); }
    }
    setLoading(false);
    setLoadingMore(false);
  }, [campaign, eventTypes, dateRange, emailSearch, contactEmail, viewScope, agentFilter]);

  // Fetch on filter change
  useEffect(() => {
    fetchData(0);
  }, [fetchData]);

  const handleShowMore = () => {
    const newOffset = offset + PAGE_SIZE;
    fetchData(newOffset, true);
  };

  const handleEmailKeyDown = (e) => {
    if (e.key === "Enter") {
      const val = e.target.value.trim().toLowerCase();
      setContactEmail(val);
      setEmailSearch(val);
    }
  };

  const clearEmailFilter = () => {
    setContactEmail("");
    setEmailSearch("");
    if (emailInputRef.current) emailInputRef.current.value = "";
  };

  const handleExportCsv = () => {
    if (!rows.length) return;
    const headers = ["Event", "Recipient", "Subject", "Campaign", "Date & Time"];
    const csvRows = rows.map(r => [
      formatEventBadge(r.event_type, false).label,
      r.email || "",
      (r.subject || "").replace(/"/g, '""'),
      campaignLabel(r.tag),
      r.created_at || "",
    ]);
    const csv = [headers.join(","), ...csvRows.map(r => r.map(c => `"${c}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `activity-export-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Merge email + platform events, sorted by time
  const realAllRows = [...rows.map(r => ({ ...r, _source: "email" })), ...platformEvents.map(r => ({ ...r, _source: "platform" }))]
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  // When the agent has no activity at all and isn't narrowing by a filter or
  // search, show a populated sample view instead of an empty state. Demo
  // interactions are sandboxed by useDemoNetworkGuard; the demo clears the
  // moment real data arrives or any filter is applied.
  const hasActiveFilter = !!campaign || eventTypes.length > 0 || !!contactEmail || !!emailSearch;
  const showDemo = !loading && realAllRows.length === 0 && !hasActiveFilter;
  useDemoNetworkGuard(showDemo);

  const demoBundle = showDemo
    ? _generateDemoData({
        campaign,
        event_types: eventTypes,
        date_range: dateRange,
        email_search: contactEmail || emailSearch,
        offset: 0,
        limit: PAGE_SIZE,
      })
    : null;

  const allRows = showDemo
    ? [
        ...((demoBundle?.data) || []).map(r => ({ ...r, _source: "email" })),
        ...((demoBundle?.platform_events) || []).map(r => ({ ...r, _source: "platform" })),
      ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    : realAllRows;

  const displayTotal = showDemo ? (demoBundle?.total || 0) : total;
  const displayPlatformCount = showDemo ? ((demoBundle?.platform_events || []).length) : platformEvents.length;
  const displayHasMore = showDemo ? false : hasMore;

  const currentDateLabel = DATE_RANGE_OPTIONS.find(o => o.value === dateRange)?.label || dateRange;

  return (
    <div style={{
      padding: isMobile ? "0 16px" : "0 32px",
      flex: 1, minWidth: 0, display: "flex", flexDirection: "column",
      background: "#f6f8fa", boxSizing: "border-box", width: "100%", overflowX: "hidden", overflowY: "auto",
      paddingBottom: showDemo ? (isMobile ? 180 : 110) : 0,
    }}>
      <div style={{ maxWidth: 1200, marginLeft: "auto", marginRight: "auto", width: "100%", flex: 1, display: "flex", flexDirection: "column" }}>

        {/* Sticky header (desktop only — mobile has no column names) */}
        <div style={{
          position: isMobile ? "static" : "sticky", top: 0, zIndex: 10, background: "#f6f8fa",
          paddingTop: isMobile ? 72 : 32, paddingBottom: 8,
          marginLeft: isMobile ? -16 : -32, marginRight: isMobile ? -16 : -32,
          paddingLeft: isMobile ? 16 : 32, paddingRight: isMobile ? 16 : 32,
        }}>
          <h1 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 700, margin: "0 0 4px 0" }}>Activity Explorer</h1>
          {!isMobile && (
            <p style={{ color: "#6b7280", fontSize: 14, marginBottom: 16, lineHeight: 1.5 }}>
              View all email events across your campaigns. Filter by campaign, event type, date range, or search for a specific contact.
            </p>
          )}

          {/* Filter bar */}
          <div style={{
            display: "flex", gap: isMobile ? 6 : 10, marginBottom: isMobile ? 10 : 16, flexWrap: "wrap", alignItems: "center",
            marginTop: isMobile ? 8 : 0,
          }}>
            {/* Email search */}
            <div style={{ position: "relative", flex: 1, minWidth: isMobile ? 140 : 220 }}>
              <FaSearch style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#9ca3af", fontSize: isMobile ? 11 : 13 }} />
              <input
                ref={emailInputRef}
                type="text"
                defaultValue={contactEmail || emailSearch}
                onKeyDown={handleEmailKeyDown}
                placeholder="Search by email…"
                style={{
                  width: "100%", padding: isMobile ? "7px 28px 7px 30px" : "9px 32px 9px 36px", borderRadius: 8,
                  border: "1px solid #e5e7eb", fontSize: isMobile ? 12 : 13, boxSizing: "border-box",
                  background: "#fff", outline: "none",
                }}
              />
              {(contactEmail || emailSearch) && (
                <FaTimes
                  onClick={clearEmailFilter}
                  style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: "#9ca3af", fontSize: 12, cursor: "pointer" }}
                />
              )}
            </div>

            {/* Campaign filter */}
            <div style={{ position: "relative", minWidth: isMobile ? 120 : 160 }}>
              <FaFilter style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#9ca3af", fontSize: isMobile ? 10 : 12 }} />
              <select
                value={campaign}
                onChange={e => setCampaign(e.target.value)}
                style={{
                  width: "100%", padding: isMobile ? "7px 10px 7px 28px" : "9px 12px 9px 32px", borderRadius: 8,
                  border: "1px solid #e5e7eb", fontSize: isMobile ? 12 : 13, boxSizing: "border-box",
                  background: "#fff", outline: "none", cursor: "pointer",
                  appearance: "none", WebkitAppearance: "none",
                }}
              >
                {CAMPAIGN_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <FaChevronDown style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: "#9ca3af", fontSize: 10, pointerEvents: "none" }} />
            </div>

            {/* Event type multi-select filter */}
            <div ref={eventDropdownRef} style={{ position: "relative", minWidth: isMobile ? 140 : 180 }}>
              <button
                onClick={() => setEventDropdownOpen(p => !p)}
                style={{
                  display: "flex", alignItems: "center", gap: 6, width: "100%",
                  padding: isMobile ? "7px 10px" : "9px 12px", borderRadius: 8, border: "1px solid #e5e7eb",
                  fontSize: isMobile ? 12 : 13, background: "#fff", cursor: "pointer", color: "#374151",
                  boxSizing: "border-box",
                }}
              >
                {eventTypes.length === 0
                  ? "All events"
                  : `${eventTypes.length} event${eventTypes.length > 1 ? "s" : ""} selected`}
                {eventTypes.length > 0 && (
                  <FaTimes
                    onClick={e => { e.stopPropagation(); setEventTypes([]); }}
                    style={{ fontSize: 10, color: "#9ca3af", marginLeft: 2, cursor: "pointer" }}
                  />
                )}
                <FaChevronDown style={{ marginLeft: "auto", color: "#9ca3af", fontSize: 10 }} />
              </button>
              {eventDropdownOpen && (
                <div style={{
                  position: "absolute", top: "100%", left: 0, marginTop: 4, minWidth: 240,
                  background: "#fff", borderRadius: 10, boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
                  border: "1px solid #e5e7eb", zIndex: 100, maxHeight: 360, overflowY: "auto",
                }}>
                  <div style={{ padding: "8px 12px 4px", fontSize: 10, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em" }}>Email Events</div>
                  {EMAIL_EVENT_OPTIONS.map(o => {
                    const checked = eventTypes.includes(o.value);
                    const cfg = EMAIL_EVENT_CONFIG[o.value];
                    return (
                      <label
                        key={o.value}
                        style={{
                          display: "flex", alignItems: "center", gap: 8,
                          padding: "7px 12px", cursor: "pointer", fontSize: 13,
                          background: checked ? "#f0fdf4" : "#fff",
                        }}
                        onMouseEnter={e => { if (!checked) e.currentTarget.style.background = "#f9fafb"; }}
                        onMouseLeave={e => { e.currentTarget.style.background = checked ? "#f0fdf4" : "#fff"; }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => setEventTypes(prev => checked ? prev.filter(v => v !== o.value) : [...prev, o.value])}
                          style={{ accentColor: "#047857" }}
                        />
                        {cfg && <cfg.Icon style={{ fontSize: 11, color: cfg.color }} />}
                        <span style={{ color: checked ? "#047857" : "#374151", fontWeight: checked ? 600 : 400 }}>{o.label}</span>
                      </label>
                    );
                  })}
                  <div style={{ padding: "10px 12px 4px", fontSize: 10, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", borderTop: "1px solid #f3f4f6" }}>Platform Events</div>
                  {PLATFORM_EVENT_OPTIONS.map(o => {
                    const checked = eventTypes.includes(o.value);
                    const cfg = PLATFORM_EVENT_CONFIG[o.value];
                    return (
                      <label
                        key={o.value}
                        style={{
                          display: "flex", alignItems: "center", gap: 8,
                          padding: "7px 12px", cursor: "pointer", fontSize: 13,
                          background: checked ? "#f0fdf4" : "#fff",
                        }}
                        onMouseEnter={e => { if (!checked) e.currentTarget.style.background = "#f9fafb"; }}
                        onMouseLeave={e => { e.currentTarget.style.background = checked ? "#f0fdf4" : "#fff"; }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => setEventTypes(prev => checked ? prev.filter(v => v !== o.value) : [...prev, o.value])}
                          style={{ accentColor: "#7c3aed" }}
                        />
                        {cfg && <cfg.Icon style={{ fontSize: 11, color: cfg.color }} />}
                        <span style={{ color: checked ? "#047857" : "#374151", fontWeight: checked ? 600 : 400 }}>{o.label}</span>
                      </label>
                    );
                  })}
                  <div style={{ padding: "10px 12px 4px", fontSize: 10, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", borderTop: "1px solid #f3f4f6" }}>SMS Events</div>
                  {SMS_EVENT_OPTIONS.map(o => {
                    const checked = eventTypes.includes(o.value);
                    const cfg = PLATFORM_EVENT_CONFIG[o.value];
                    return (
                      <label
                        key={o.value}
                        style={{
                          display: "flex", alignItems: "center", gap: 8,
                          padding: "7px 12px", cursor: "pointer", fontSize: 13,
                          background: checked ? "#f0fdf4" : "#fff",
                        }}
                        onMouseEnter={e => { if (!checked) e.currentTarget.style.background = "#f9fafb"; }}
                        onMouseLeave={e => { e.currentTarget.style.background = checked ? "#f0fdf4" : "#fff"; }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => setEventTypes(prev => checked ? prev.filter(v => v !== o.value) : [...prev, o.value])}
                          style={{ accentColor: "#16a34a" }}
                        />
                        {cfg && <cfg.Icon style={{ fontSize: 11, color: cfg.color }} />}
                        <span style={{ color: checked ? "#047857" : "#374151", fontWeight: checked ? 600 : 400 }}>{o.label}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Date range dropdown */}
            <div ref={dateDropdownRef} style={{ position: "relative", minWidth: isMobile ? 120 : 150 }}>
              <button
                onClick={() => setDateDropdownOpen(p => !p)}
                style={{
                  display: "flex", alignItems: "center", gap: 6, width: "100%",
                  padding: isMobile ? "7px 10px" : "9px 12px", borderRadius: 8, border: "1px solid #e5e7eb",
                  fontSize: isMobile ? 12 : 13, background: "#fff", cursor: "pointer", color: "#374151",
                }}
              >
                <FaCalendarAlt style={{ color: "#9ca3af", fontSize: 12 }} />
                {currentDateLabel}
                <FaChevronDown style={{ marginLeft: "auto", color: "#9ca3af", fontSize: 10 }} />
              </button>
              {dateDropdownOpen && (
                <div style={{
                  position: "absolute", top: "100%", left: 0, marginTop: 4, minWidth: 180,
                  background: "#fff", borderRadius: 10, boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
                  border: "1px solid #e5e7eb", zIndex: 100, overflow: "hidden",
                }}>
                  {DATE_RANGE_OPTIONS.map(o => (
                    <div
                      key={o.value}
                      onClick={() => { setDateRange(o.value); setDateDropdownOpen(false); }}
                      style={{
                        padding: "10px 16px", fontSize: 13, cursor: "pointer",
                        background: dateRange === o.value ? "#ecfdf5" : "#fff",
                        color: dateRange === o.value ? "#047857" : "#374151",
                        fontWeight: dateRange === o.value ? 600 : 400,
                      }}
                      onMouseEnter={e => { if (dateRange !== o.value) e.currentTarget.style.background = "#f9fafb"; }}
                      onMouseLeave={e => { if (dateRange !== o.value) e.currentTarget.style.background = "#fff"; }}
                    >
                      {o.label}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Export button */}
            <button
              onClick={handleExportCsv}
              disabled={!rows.length}
              style={{
                display: "flex", alignItems: "center", gap: 6, padding: "9px 14px",
                borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 13,
                background: "#fff", cursor: rows.length ? "pointer" : "default",
                color: rows.length ? "#374151" : "#9ca3af",
              }}
            >
              <FaDownload style={{ fontSize: 11 }} />
              Export
            </button>
          </div>

          {/* Sticky table header – inside the sticky section so it stays visible */}
          {!loading && allRows.length > 0 && (
            <>
              <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 10 }}>
                {contactEmail ? (
                  <>Showing events for <strong style={{ color: "#374151" }}>{contactEmail}</strong> · </>
                ) : null}
                {(displayTotal + displayPlatformCount).toLocaleString()} total event{(displayTotal + displayPlatformCount) !== 1 ? "s" : ""}
                {!showDemo && rows.length < displayTotal ? ` · Showing ${allRows.length}` : ""}
              </div>
              <div style={{
                display: isMobile ? "none" : "grid",
                gridTemplateColumns: "160px 220px 1fr 140px 170px",
                padding: "10px 20px", borderBottom: "2px solid #e5e7eb",
                background: "#fafbfc", fontSize: 11, fontWeight: 600,
                textTransform: "uppercase", color: "#6b7280", letterSpacing: "0.04em",
                border: "1px solid #e5e7eb", borderRadius: "12px 12px 0 0",
                boxShadow: "0 2px 8px #0001",
              }}>
                <div>Event</div>
                <div>Recipient</div>
                <div>Subject</div>
                <div>Tag</div>
                <div style={{ textAlign: "right" }}>Date & Time</div>
              </div>
            </>
          )}
        </div>

        {/* Results */}
        {loading ? (
          <div style={{
            background: "#fff", borderRadius: "0 0 12px 12px", boxShadow: "0 1px 4px #0001",
            border: "1px solid #e5e7eb", overflow: "hidden",
          }}>
            {[...Array(6)].map((_, i) => (
              <div key={i} style={{
                display: "flex", alignItems: "center", gap: 14,
                padding: isMobile ? "12px 14px" : "12px 20px",
                borderBottom: i < 5 ? "1px solid #f3f4f6" : "none",
              }}>
                <Skeleton width={80} height={20} borderRadius={10} />
                <Skeleton width={140} height={13} />
                <div style={{ flex: 1 }}><Skeleton width="80%" height={13} /></div>
                {!isMobile && <Skeleton width={60} height={20} borderRadius={10} />}
                <Skeleton width={100} height={12} />
              </div>
            ))}
          </div>
        ) : allRows.length === 0 ? (
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{
            background: "#fff", borderRadius: 12, padding: 40,
            boxShadow: "0 2px 8px #0001", border: "1px solid #e5e7eb", textAlign: "center",
          }}>
            <FaEnvelope style={{ fontSize: 36, color: "#d1d5db", marginBottom: 12 }} />
            <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8, color: "#1f2937" }}>No Events Found</h2>
            <p style={{ fontSize: 14, color: "#6b7280" }}>
              Try adjusting your filters or date range.
            </p>
          </div>
          </div>
        ) : (
          <>
            {/* Table */}
            <div style={{
              background: "#fff", borderRadius: "0 0 12px 12px", boxShadow: "0 2px 8px #0001",
              borderLeft: "1px solid #e5e7eb", borderRight: "1px solid #e5e7eb", borderBottom: "1px solid #e5e7eb",
              overflow: "hidden", marginBottom: 24,
            }}>

              {/* Data rows */}
              {allRows.map((row, idx) => {
                const isPlatform = row._source === "platform";
                const isSms = row.event_type === "sms_sent" || row.event_type === "sms_unsubscribe";
                const cfg = formatEventBadge(row.event_type, isPlatform);
                const tag = campaignLabel(row.tag);
                const relative = formatRelative(row.created_at);
                const recipient = row.email || row.phone || "";

                if (isMobile) {
                  return (
                    <div
                      key={idx}
                      style={{
                        padding: "14px 16px",
                        borderBottom: idx < allRows.length - 1 ? "1px solid #f3f4f6" : "none",
                        overflow: "hidden", minWidth: 0,
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                        <span title={cfg.desc || ""} style={{
                          display: "inline-flex", alignItems: "center", gap: 4,
                          borderRadius: 14, padding: "3px 10px", fontWeight: 600, fontSize: 11,
                          background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
                          cursor: cfg.desc ? "help" : "default",
                        }}>
                          <cfg.Icon style={{ fontSize: 10 }} /> {cfg.label}
                        </span>
                        {(() => {
                          const norm = normaliseCampaign(row.tag);
                          const ccfg = CAMPAIGN_CONFIG[norm];
                          if (ccfg) {
                            return (
                              <span style={{
                                fontSize: 10, borderRadius: 12, padding: "2px 8px",
                                background: ccfg.bg, color: ccfg.color, border: `1px solid ${ccfg.border}`,
                              }}>
                                {ccfg.label}
                              </span>
                            );
                          }
                          if (tag) {
                            return (
                              <span style={{
                                fontSize: 10, borderRadius: 12, padding: "2px 8px",
                                background: "#f1f5f9", color: "#64748b", border: "1px solid #e2e8f0",
                              }}>
                                {tag}
                              </span>
                            );
                          }
                          return null;
                        })()}
                      </div>
                      {recipient && (
                        isSms ? (
                          <span
                            style={{
                              display: "inline-flex", alignItems: "center", gap: 4,
                              padding: "2px 10px", borderRadius: 14, fontSize: 12,
                              background: "#f0fdf4", color: "#166534",
                              border: "1px solid #bbf7d0", marginBottom: 2,
                            }}
                          >
                            {recipient}
                          </span>
                        ) : (
                          <a
                            href={`/user-activity?email=${encodeURIComponent(recipient)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: "inline-flex", alignItems: "center", gap: 4,
                              padding: "2px 10px", borderRadius: 14, fontSize: 12,
                              background: "#f0f4f8", color: "#1d4ed8", textDecoration: "none",
                              border: "1px solid #dbeafe", cursor: "pointer", marginBottom: 2,
                            }}
                          >
                            {recipient}
                          </a>
                        )
                      )}
                      {isPlatform && row.detail && (
                        <div style={{ fontSize: 12, color: "#374151", marginBottom: 2 }}>{row.detail}</div>
                      )}
                      {isPlatform && row.page_url && (() => {
                        try {
                          const u = new URL(row.page_url);
                          const slug = u.pathname.split('/').filter(Boolean).pop() || u.pathname;
                          return <a href={row.page_url} target="_blank" rel="noopener noreferrer" title={u.pathname} style={{ fontSize: 11, color: "#9ca3af", textDecoration: "none", display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginBottom: 2 }} onMouseEnter={e => e.currentTarget.style.color = '#6b7280'} onMouseLeave={e => e.currentTarget.style.color = '#9ca3af'}>{slug}</a>;
                        } catch { return null; }
                      })()}
                      {!isPlatform && row.subject && (
                        <div style={{ fontSize: 12, color: "#6b7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginBottom: 2, maxWidth: "100%" }}>{row.subject}</div>
                      )}
                      {!isPlatform && row.clicked_url && (
                        <a
                          href={row.clicked_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={row.clicked_url}
                          style={{ fontSize: 11, color: "#4338ca", textDecoration: "none", display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginBottom: 2, maxWidth: "100%" }}
                        >
                          {row.clicked_url}
                        </a>
                      )}
                      {!isPlatform && row.event_type === "Click" && row.country && (
                        <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 2 }} title="Click origin country (Postmark Geo)">
                          🌍 {row.country}
                        </div>
                      )}
                      {!isPlatform && !isSms && row.message_id && (
                        <a
                          href={`/email-details/${encodeURIComponent(row.message_id)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ fontSize: 11, color: "#0369a1", textDecoration: "none", display: "inline-block", marginBottom: 2 }}
                        >
                          View email →
                        </a>
                      )}
                      <div style={{ fontSize: 11, color: "#9ca3af" }}>
                        {formatTimestamp(row.created_at)} {formatTime(row.created_at)}
                        {relative && <span style={{ marginLeft: 6 }}>({relative})</span>}
                      </div>
                    </div>
                  );
                }

                return (
                  <div
                    key={idx}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "160px 220px 1fr 140px 170px",
                      padding: "12px 20px",
                      borderBottom: idx < allRows.length - 1 ? "1px solid #f3f4f6" : "none",
                      alignItems: "center",
                      fontSize: 13,
                      transition: "background 0.1s",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = "#fafbfc"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "#fff"; }}
                  >
                    {/* Event badge */}
                    <div>
                      <span title={cfg.desc || ""} style={{
                        display: "inline-flex", alignItems: "center", gap: 4,
                        borderRadius: 14, padding: "3px 10px", fontWeight: 600, fontSize: 11,
                        background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
                        whiteSpace: "nowrap", cursor: cfg.desc ? "help" : "default",
                      }}>
                        <cfg.Icon style={{ fontSize: 10 }} /> {cfg.label}
                      </span>
                    </div>

                    {/* Recipient */}
                    <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#374151", paddingRight: 12 }}>
                      {recipient ? (
                        isSms ? (
                          <span
                            style={{
                              display: "inline-flex", alignItems: "center", gap: 4,
                              padding: "2px 10px", borderRadius: 14, fontSize: 12,
                              background: "#f0fdf4", color: "#166534",
                              border: "1px solid #bbf7d0", maxWidth: "100%",
                              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                            }}
                          >
                            {recipient}
                          </span>
                        ) : (
                          <a
                            href={`/user-activity?email=${encodeURIComponent(recipient)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={e => e.stopPropagation()}
                            style={{
                              display: "inline-flex", alignItems: "center", gap: 4,
                              padding: "2px 10px", borderRadius: 14, fontSize: 12,
                              background: "#f0f4f8", color: "#1d4ed8", textDecoration: "none",
                              border: "1px solid #dbeafe", cursor: "pointer", maxWidth: "100%",
                              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                            }}
                            onMouseEnter={e => { e.currentTarget.style.background = "#dbeafe"; }}
                            onMouseLeave={e => { e.currentTarget.style.background = "#f0f4f8"; }}
                          >
                            {recipient}
                          </a>
                        )
                      ) : "—"}
                    </div>

                    {/* Subject */}
                    <div style={{ overflow: "hidden", textOverflow: "ellipsis", color: "#6b7280", paddingRight: 12, minWidth: 0 }}>
                      {isPlatform ? (
                        <>
                          {row.detail && <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "#374151" }}>{row.detail}</div>}
                          {row.page_url && (() => {
                            try {
                              const u = new URL(row.page_url);
                              const slug = u.pathname.split('/').filter(Boolean).pop() || u.pathname;
                              return <a href={row.page_url} target="_blank" rel="noopener noreferrer" title={u.pathname} style={{ fontSize: 11, color: "#9ca3af", textDecoration: "none", display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} onMouseEnter={e => e.currentTarget.style.color = '#6b7280'} onMouseLeave={e => e.currentTarget.style.color = '#9ca3af'}>{slug}</a>;
                            } catch { return null; }
                          })()}
                          {!row.detail && !row.page_url && <span>{PLATFORM_EVENT_CONFIG[row.event_type]?.label || row.event_type}</span>}
                        </>
                      ) : (
                        <>
                          <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.subject || "\u2014"}</div>
                          {row.clicked_url && (
                            <a
                              href={row.clicked_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={row.clicked_url}
                              style={{ fontSize: 11, color: "#4338ca", textDecoration: "none", display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                            >
                              {row.clicked_url}
                            </a>
                          )}
                          {row.event_type === "Click" && row.country && (
                            <div style={{ fontSize: 11, color: "#6b7280" }} title="Click origin country (Postmark Geo)">
                              🌍 {row.country}
                            </div>
                          )}
                          {!isSms && row.message_id && (
                            <a
                              href={`/email-details/${encodeURIComponent(row.message_id)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={e => e.stopPropagation()}
                              style={{ fontSize: 11, color: "#0369a1", textDecoration: "none", display: "inline-block" }}
                            >
                              View email →
                            </a>
                          )}
                        </>
                      )}
                    </div>

                    {/* Campaign tag */}
                    <div>
                      {(() => {
                        const norm = normaliseCampaign(row.tag);
                        const ccfg = CAMPAIGN_CONFIG[norm];
                        if (ccfg) {
                          return (
                            <span style={{
                              fontSize: 11, borderRadius: 12, padding: "2px 10px",
                              background: ccfg.bg, color: ccfg.color, border: `1px solid ${ccfg.border}`,
                              whiteSpace: "nowrap",
                            }}>
                              {ccfg.label}
                            </span>
                          );
                        }
                        if (isSms) {
                          return (
                            <span style={{
                              fontSize: 11, borderRadius: 12, padding: "2px 10px",
                              background: "#dcfce7", color: "#166534", border: "1px solid #86efac",
                              whiteSpace: "nowrap",
                            }}>
                              sms
                            </span>
                          );
                        }
                        if (isPlatform) {
                          return (
                            <span style={{
                              fontSize: 11, borderRadius: 12, padding: "2px 10px",
                              background: "#f3e8ff", color: "#7c3aed", border: "1px solid #c4b5fd",
                              whiteSpace: "nowrap",
                            }}>
                              platform
                            </span>
                          );
                        }
                        return tag ? (
                          <span style={{
                            fontSize: 11, borderRadius: 12, padding: "2px 10px",
                            background: "#f1f5f9", color: "#64748b", border: "1px solid #e2e8f0",
                            whiteSpace: "nowrap",
                          }}>
                            {tag}
                          </span>
                        ) : "—";
                      })()}
                    </div>

                    {/* Date / Time */}
                    <div style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      <div style={{ color: "#374151", fontSize: 12 }}>
                        {formatTimestamp(row.created_at)}, {formatTime(row.created_at)}
                      </div>
                      {relative && <div style={{ fontSize: 11, color: "#9ca3af" }}>{relative}</div>}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Show more */}
            {displayHasMore && (
              <div style={{ textAlign: "center", marginBottom: 32 }}>
                <button
                  onClick={handleShowMore}
                  disabled={loadingMore}
                  style={{
                    padding: "10px 28px", borderRadius: 8, border: "1px solid #e5e7eb",
                    background: "#fff", fontSize: 13, fontWeight: 600, color: "#374151",
                    cursor: loadingMore ? "default" : "pointer",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                  }}
                >
                  {loadingMore ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <>Show more</>
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>
      {showDemo && (
        <DemoBanner
          isMobile={isMobile}
          title="Here's how Activity Explorer looks with engagement"
          subtitle="Sample data — every email open, click, SMS send and on-platform action from your contacts will appear here once your campaigns are running."
        />
      )}
    </div>
  );
}
